import useForm from 'pages/components/Form/useForm';
import FormFields from 'pages/components/FormFields';

import { Grid2 } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { User, UserEntity } from 'Domain/User/Entity/User';
import { UserProfile } from 'Domain/User/Entity/UserProfile';
import { CreditorData } from 'Domain/User/Entity/CreditorData';
import ProfileFormSections from './FormSections';
import PersonalInfosConfig from './FormSections/FormConfigs/PersonalInfosConfig';
import LocationConfig from './FormSections/FormConfigs/LocationConfig';
import ContactConfig from './FormSections/FormConfigs/ContactConfig';
import ProfessionalInfosConfig from './FormSections/FormConfigs/ProfessionalInfosConfig';
import { useEffect } from 'react';
import { UpdateUserRequest } from 'Domain/User/Dto/UpdateUserRequest';
import {
  getCurrentUserData,
  updateUserData,
} from 'Application/User/UserService';
import { getApiUrl } from 'Infrastructure/WebClient';
import { CURRENT_USER_CONTRACT, USER_CONTRACT } from 'Application/RequestTypes';
import { eventDispatcher } from "pages/Hooks/useEventListener";
import { OPEN_CONTRACT_MODAL } from 'pages/constants/EventKeys';

type ProfileFormProps = {
  userData: User;
  setUserData: Function;
  id: string;
};

export default function ProfileForm({
  userData,
  id,
  setUserData,
}: ProfileFormProps) {
  const userProfile: UserProfile = userData.profile;
  const creditorData = userData.creditorData;
  const ProfileFormConfig = {
    ...PersonalInfosConfig,
    ...ProfessionalInfosConfig,
    ...LocationConfig,
    ...ContactConfig,
  };

  for (var field in ProfileFormConfig) {
    if (field in userProfile) {
      ProfileFormConfig[field].default =
        userProfile[field as keyof UserProfile];
    }
    if (creditorData && field in creditorData) {
      ProfileFormConfig[field].default =
        creditorData[field as keyof CreditorData];
    }
  }

  ProfileFormConfig.city.default = {
    label: userProfile.city,
    value: userProfile.postCode,
  };

  const {
    errors,
    values,
    options,
    handleInputChange,
    isValidForm,
    formatErrorObject,
    Form,
  } = useForm(ProfileFormConfig, true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  function handleSubmit(e: any) {
    e.preventDefault();
    if (isValidForm()) {
      updateUserData(id, getUpdateUserRequest(values)).then((res) => {
        setUserData(res);
        if ("creditorData" in res && res.creditorData?.status === "COMPLETED"){
          eventDispatcher(OPEN_CONTRACT_MODAL)
        }
      });
    }
  }

  useEffect(() => {
    isValidForm();
  }, []);

  useEffect(() => {
    if (values.externalIdentifier) {
      handleInputChange({
        target: {
          name: 'externalIdentifier',
          value: values.externalIdentifier,
        },
      });
    }
  }, [values.country]);

  const gridStyle = isMobile
    ? {
        container: '1fr',
      }
    : {
        container: 'repeat(3, 1fr)',
        contact: '3 / 1 / 4 / 4',
        personal: '1 / 1 / 3 / 2',
        professional: '1 / 2 / 3 / 3',
        location: ' 1 / 3 / 2 / 4',
        contract: '2 / 3 / 3 / 4',
      };

  const contractUrl = getCurrentUserData().isUserInternal()
    ? getApiUrl(USER_CONTRACT, { id: id })
    : getApiUrl(CURRENT_USER_CONTRACT);
  return (
    <Form onSubmit={handleSubmit}>
      <Grid2
        display={'grid'}
        gridTemplateColumns={gridStyle.container}
        gap={2}
        container
      >
        <Grid2 gridArea={gridStyle.personal}>
          <ProfileFormSections.PersonalInfos
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            errors={errors}
          />
        </Grid2>
        <Grid2 gridArea={gridStyle.professional}>
          <ProfileFormSections.ProfessionalInfos
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            isMobile={isMobile}
            errors={errors}
          />
        </Grid2>

        <Grid2 gridArea={gridStyle.location}>
          <ProfileFormSections.Location
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            isMobile={isMobile}
            errors={errors}
          />
        </Grid2>
        <Grid2 gridArea={gridStyle.contact}>
          <ProfileFormSections.Contact
            values={values}
            handleInputChange={handleInputChange}
            options={options}
            formatErrorObject={formatErrorObject}
            isMobile={isMobile}
            errors={errors}
          />
        </Grid2>
        <Grid2 gridArea={gridStyle.contract}>
          <ProfileFormSections.Contract
            disabled={!(new UserEntity(userData)).isUserContractVisible()}
            isSigned={!!(creditorData?.contractSigned)}
            userId={id}
            contractUrl={contractUrl}
            setUserData={setUserData}
          />
        </Grid2>
      </Grid2>

      <FormFields.Button
        type="submit"
        label={t('FORM.SUBMIT.update')}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Form>
  );
}

function getUpdateUserRequest(values: any) {
  const updateUserRequest = new UpdateUserRequest(
    values.email,
    values.gender,
    values.language,
    values.firstName,
    values.lastName,
    values.profession,
    values.specialty,

    values.country,
    values.city.label,
    values.city.value,
    values.boxNumber,
    values.box,
    values.address,
    values.phone,
    values.mobile,

    values.iban,
    values.professionalNumber,
    values.companyName,
    values.companyNumber,
    values.birthDate,
    values.externalIdentifier,

  );

  return updateUserRequest;
}
